import React, { useMemo } from 'react';
import { Document as PdfDocument, Page } from 'react-pdf';
import { Box, Spinner, VStack } from '@chakra-ui/react';
import { PdfFileSource } from '@/hooks/viewers/usePdfViewer.ts';

interface PdfDocumentRendererProps {
	pdfOptions: PdfFileSource;
	pageNumber: number;
	width?: number; // used when scale is not provided
	scale?: number; // explicit zoom level; if provided, overrides width
	rotate?: number;
	loading?: boolean;
	onLoadSuccess: ({ numPages }: { numPages: number }) => void;
	onLoadError: (error: Error) => void;
	containerProps?: React.ComponentProps<typeof Box>;
	spinnerColor?: string;
	spinnerSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	showAllPages?: boolean; // when true, displays all pages instead of single page
	numPages?: number; // required when showAllPages is true
	onBackgroundClick?: () => void; // called when clicking on empty space (not on PDF pages)
	onPageLoadSuccess?: (page: unknown, pageNumber: number) => void;
	onPageElementRef?: (pageNumber: number, element: HTMLElement | null) => void;
}

/**
 * Shared PDF document renderer component for consistent PDF rendering across the application
 *
 * Handles PDF.js integration with React components, managing document loading,
 * page rendering, and error states. Key constraints:
 * - When width is specified, height is ignored by PDF.js (maintains aspect ratio)
 * - Scale prop overrides width when both are provided
 * - Text and annotation layers are disabled for performance in preview contexts
 */
const PdfDocumentRenderer: React.FC<PdfDocumentRendererProps> = (props) => {
	const {
		pdfOptions,
		pageNumber,
		width,
		scale,
		rotate,
		loading = false,
		onLoadSuccess,
		onLoadError,
		containerProps = {},
		spinnerColor = 'primary',
		spinnerSize = 'lg',
		showAllPages = false,
		numPages = 0,
		onBackgroundClick,
		onPageLoadSuccess,
		onPageElementRef,
	} = props;

	/**
	 * Memoizes PDF options to prevent unnecessary document reloads
	 *
	 * PDF.js Document component will reload the entire PDF when props change,
	 * which is expensive. Memoization ensures reloads only occur when the actual
	 * URL or credentials change, not when the parent component re-renders.
	 */
	const memoizedPdfOptions = useMemo(
		() => pdfOptions,
		[
			'data' in pdfOptions ? pdfOptions.data : pdfOptions.url,
			'withCredentials' in pdfOptions ? pdfOptions.withCredentials : undefined,
		],
	);

	/**
	 * Handles clicks on the PDF container to distinguish between PDF page clicks and background clicks
	 *
	 * When clicking directly on the container background (not on PDF pages), triggers the onBackgroundClick callback.
	 * This allows the parent component to implement close-on-background-click behavior while keeping PDF pages interactive.
	 */
	const handleContainerClick = (e: React.MouseEvent) => {
		// Only trigger background click if the click target is the container itself (not a child element like PDF pages)
		if (e.target === e.currentTarget && onBackgroundClick) {
			onBackgroundClick();
		}
	};

	return (
		<Box
			className="pdf-document-renderer-container"
			display="flex"
			justifyContent="center"
			alignItems="center"
			onClick={handleContainerClick}
			{...containerProps}
		>
			{loading && <Spinner size={spinnerSize} color={spinnerColor} />}

			<PdfDocument file={memoizedPdfOptions} onLoadSuccess={onLoadSuccess} onLoadError={onLoadError} loading="">
				{/* 
 				Conditional rendering based on display mode:
 				- showAllPages: renders all pages in a vertical stack (for full document view)
 				- default: renders single page (for preview/navigation contexts)
 				
 				Text and annotation layers are disabled for performance in preview scenarios
 			*/}
				{showAllPages && numPages > 0 ? (
					<VStack gap={4} align="center" mb={24}>
						{Array.from({ length: numPages }, (_, index) => (
							<Box
								key={index + 1}
								data-pdf-page={index + 1}
								ref={(element: HTMLDivElement | null) => onPageElementRef?.(index + 1, element)}
							>
								<Page
									pageNumber={index + 1}
									{...(scale ? { scale } : { width })}
									rotate={rotate}
									onLoadSuccess={(page) => onPageLoadSuccess?.(page, index + 1)}
									renderTextLayer={false}
									renderAnnotationLayer={false}
								/>
							</Box>
						))}
					</VStack>
				) : (
					<Page
						pageNumber={pageNumber}
						{...(scale ? { scale } : { width })}
						rotate={rotate}
						onLoadSuccess={(page) => onPageLoadSuccess?.(page, pageNumber)}
						renderTextLayer={false}
						renderAnnotationLayer={false}
					/>
				)}
			</PdfDocument>
		</Box>
	);
};

export default PdfDocumentRenderer;
